import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api-client';
import { countryLabel, presentableCountryCodes } from '../lib/countries';
import { formatDayMonthYear, formatIsoDate, formatOooUntil, parseIsoDate } from '../lib/dates';
import { skipsText } from '../lib/revisionText';
import { partialCancellationFrom } from '../lib/timeOffCancellation';
import type {
  EnrolledBalanceDto,
  HolidayDto,
  PendingApprovalEntry,
  TimeOffRequestDto,
} from '../lib/types';
import { UNSIGNED_REQUEST_MESSAGE, invalidateLeaveDocuments } from '../lib/useLeaveDocuments';
import { useMyEmployee } from '../lib/useMyEmployee';
import { Button, CardQueryState, useToast } from '@payforge/ui';
import { usePermissions } from '../lib/usePermissions';
import { useTimeOffCategories } from '../lib/useTimeOffCategories';
import { WidgetCard } from '../components/WidgetCard';
import { BalanceTileGrid } from '../components/BalanceTileGrid';
import { TimeOffDecisionDialog } from '../components/TimeOffDecisionDialog';
import { balanceToTile } from '../lib/balanceTiles';
import { formatDays } from '../lib/formatDays';
import { WidgetEmptyState } from '../components/WidgetEmptyState';
import { SummaryRow } from '../components/SummaryRow';
import { WidgetCtaButton } from '../components/WidgetCtaButton';
import { TimeOffIconSvg } from '../components/TimeOffIconSvg';
import { ChevronDownIcon } from '../components/icons';
import timeOffEmptyArt from '../assets/illustrations/Timeoff.png';
import companyHolidaysEmptyArt from '../assets/illustrations/companyholidays.png';
import timeOffRequestsEmptyArt from '../assets/illustrations/timeoffrequests.png';
import { RequestTimeOffDrawer } from './RequestTimeOffDrawer';

// Spec-literal empty state messages extracted as named constants (§W1, §W7).
const W1_EMPTY_NO_POLICIES = 'No time-off balances yet. Contact HR if this is unexpected.';
const W7_EMPTY_NO_HOLIDAYS = 'No holidays scheduled for the rest of the year.';

// Time Off card (W1). Reads /home/time-off which returns EnrolledBalanceDto[] — one entry per
// enrolled category. Traditional policies show available days; flexible show "Unlimited".
// The Request Time Off dialog is filtered to enrolled categories only.

export function TimeOffCard() {
  const navigate = useNavigate();
  const { canEdit } = usePermissions();
  const canRequest = canEdit('PROFILE_TIMEOFF');
  const balancesQuery = useQuery({
    queryKey: ['home', 'time-off'],
    queryFn: () => api.get<EnrolledBalanceDto[]>('/v1/home/time-off'),
  });
  const me = useMyEmployee({ enabled: false });
  const enrollmentsQuery = useQuery({
    queryKey: ['time-off', 'enrollments', 'mine'],
    queryFn: () => api.get<{ categoryId: number; categoryName: string; icon: string; color: string; policyId: number; policyName: string; policyType: string }[]>('/v1/time-off/enrollments/mine'),
  });

  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);

  async function openDialog(e: React.MouseEvent) {
    e.stopPropagation();
    await Promise.all([me.refetch(), enrollmentsQuery.refetch()]);
    setDialogOpen(true);
  }

  /**
   * Closes the drawer on a request that needs nothing more here and refreshes what it changed. A
   * request still waiting for its own leave form only gets here when the form did not open.
   */
  function handleSubmitted(created: TimeOffRequestDto) {
    setDialogOpen(false);
    void queryClient.invalidateQueries({ queryKey: ['time-off'] });
    void queryClient.invalidateQueries({ queryKey: ['home'] });
    void queryClient.invalidateQueries({ queryKey: ['calendar'] });
    invalidateLeaveDocuments(queryClient);
    if (created.signingStage === 'EMPLOYEE') showToast(UNSIGNED_REQUEST_MESSAGE, 'info');
  }

  function handleBodyClick() {
    void navigate('/me#time-off');
  }

  // Guard against a non-array response so an unexpected shape never crashes the
  // whole home page (the card just renders its empty state instead).
  const balanceList = Array.isArray(balancesQuery.data) ? balancesQuery.data : [];
  const enrollmentList = Array.isArray(enrollmentsQuery.data) ? enrollmentsQuery.data : [];

  // One illustrated empty state, with copy that reflects the underlying cause.
  const empty =
    enrollmentList.length > 0
      ? {
          title: 'No time off yet',
          description: 'Time off balances will appear here when available.',
        }
      : {
          title: W1_EMPTY_NO_POLICIES,
          description: undefined,
        };
  const emptyNode = (
    <WidgetEmptyState image={timeOffEmptyArt} title={empty.title} description={empty.description} />
  );

  const hasNoEnrollments = enrollmentList.length === 0;

  const enrolledCategories = enrollmentList.map((e) => ({
    id: e.categoryId,
    code: '',
    name: e.categoryName,
    color: e.color,
    icon: e.icon,
    paid: true,
    calendarVisible: true,
    sortOrder: 0,
  }));

  return (
    <>
      <WidgetCard
        icon={<TimeOffIconSvg icon="calendar" className="h-5 w-5" />}
        title="Time Off"
        className="cursor-pointer"
        rootProps={{
          'data-testid': 'time-off-card',
          onClick: handleBodyClick,
          tabIndex: 0,
          'aria-label': 'Time Off — view all balances',
          onKeyDown: (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleBodyClick();
            }
          },
        }}
      >
        <CardQueryState
          query={balancesQuery}
          label="Time Off"
          skeleton={
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-[104px] animate-pulse rounded-xl bg-slate-100" />
              ))}
            </div>
          }
          empty={{
            when: balanceList.length === 0,
            node: emptyNode,
          }}
        >
          <BalanceTileGrid
            tiles={balanceList.map((b) => balanceToTile(b, 'days'))}
          />
        </CardQueryState>

        <WidgetCtaButton
          icon={<TimeOffIconSvg icon="calendar" className="h-4 w-4" />}
          label="Request time off"
          onClick={openDialog}
          disabled={hasNoEnrollments || !canRequest}
          title={
            !canRequest
              ? 'Your access level allows viewing time off only'
              : hasNoEnrollments
                ? "You're not enrolled in any time-off policies"
                : undefined
          }
        />
      </WidgetCard>

      {dialogOpen && me.data ? (
        <RequestTimeOffDrawer
          employeeId={me.data.id}
          subjectLocationId={me.data.locationId}
          categories={enrolledCategories}
          balances={balanceList}
          onClose={() => setDialogOpen(false)}
          onSubmitted={handleSubmitted}
        />
      ) : null}
    </>
  );
}

type WhosOutEntry = {
  kind: 'TIME_OFF' | 'HOLIDAY';
  id: number;
  employeeId: number | null;
  displayName: string | null;
  categoryName: string | null;
  categoryColor: string | null;
  note: string | null; // admin-only; null for non-admin viewers (server-enforced)
  holidayName: string | null;
  holidayCountryCodes: string[] | null;
  startDate: string;
  endDate: string;
};

// True when an entry's [startDate, endDate] range covers the given day (inclusive).
function coversDay(entry: WhosOutEntry, dayIso: string): boolean {
  return entry.startDate <= dayIso && entry.endDate >= dayIso;
}

// Distinct people (by employeeId) whose time off covers today; first entry per person wins.
function peopleOutToday(entries: WhosOutEntry[], todayIso: string): WhosOutEntry[] {
  const seen = new Set<number>();
  const out: WhosOutEntry[] = [];
  for (const e of entries) {
    if (e.kind !== 'TIME_OFF' || e.employeeId === null) continue;
    if (!coversDay(e, todayIso)) continue;
    if (seen.has(e.employeeId)) continue;
    seen.add(e.employeeId);
    out.push(e);
  }
  return out;
}

// One "Out today" holiday row. A public holiday closes an office, so the observing country is
// the "who" and the holiday name leads the meta. `key` is the row's identity (country code plus
// holiday name), which is what the rows are deduplicated on.
type HolidayRow = { key: string; title: string; meta: string };

// Expands one holiday into a row per observing country. The meta keeps the sibling widget's
// "Company Holiday" wording after the holiday name, because a bare country title is otherwise
// indistinguishable from a person row (Georgia, Jordan and Chad are also given names). A holiday
// with no country code (a location saved without one) keeps the old rendering: its own name as
// the title and "Company Holiday" alone as the meta.
function holidayRows(entry: WhosOutEntry): HolidayRow[] {
  const name = entry.holidayName ?? 'Holiday';
  const codes = presentableCountryCodes(entry.holidayCountryCodes);
  if (codes.length === 0) return [{ key: `h-|${name}`, title: name, meta: 'Company Holiday' }];
  return codes.map((code) => ({
    key: `h-${code}|${name}`,
    title: countryLabel(code),
    meta: `${name} · Company Holiday`,
  }));
}

// Holiday rows in effect today, deduplicated by country code + holiday name so one national
// holiday recorded once per office renders as a single country row. Sorted by country name
// because every row of a given day ties on start and end date, which leaves the order the API
// returns them in undefined.
function holidayRowsToday(entries: WhosOutEntry[], todayIso: string): HolidayRow[] {
  const seen = new Set<string>();
  const rows: HolidayRow[] = [];
  for (const e of entries) {
    if (e.kind !== 'HOLIDAY' || !coversDay(e, todayIso)) continue;
    for (const row of holidayRows(e)) {
      if (seen.has(row.key)) continue;
      seen.add(row.key);
      rows.push(row);
    }
  }
  return rows.sort((a, b) => a.title.localeCompare(b.title));
}

// ISO day string N calendar days after the given day (local time).
function addDays(dayIso: string, n: number): string {
  const d = parseIsoDate(dayIso);
  d.setDate(d.getDate() + n);
  return formatIsoDate(d);
}

// Time-off entries whose leave STARTS within the next 7 calendar days: start date
// in [tomorrow .. today+7] inclusive. A leave starting today belongs to "Out today"
// only, so it is excluded here. The same person may appear both here and in "Out
// today" via a separate future leave, so every qualifying entry is kept (no dedup).
function upcomingLeaves(entries: WhosOutEntry[], todayIso: string): WhosOutEntry[] {
  const windowStart = addDays(todayIso, 1);
  const windowEnd = addDays(todayIso, 7);
  return entries.filter(
    (e) =>
      e.kind === 'TIME_OFF' &&
      e.employeeId !== null &&
      e.startDate >= windowStart &&
      e.startDate <= windowEnd,
  );
}

// Display name for a person entry; falls back to "Employee #<id>" when the name is withheld.
function nameOf(entry: WhosOutEntry): string {
  return entry.displayName ?? `Employee #${entry.employeeId}`;
}

// Disclosure chevron that rotates when its parent <details> is open (matches the
// Onboarding widget's group chevron).
function WhosOutDisclosureChevron() {
  return (
    <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform group-open:rotate-180" />
  );
}

// One person row inside a group: name on the left, a leave-meta line on the right;
// the whole row links to the person's profile.
function PersonEntryRow({ entry, meta }: { entry: WhosOutEntry; meta: string }) {
  return (
    <li>
      <Link
        to={`/people/${entry.employeeId}`}
        className="-mx-1 flex items-center justify-between gap-3 rounded px-1 py-1 text-sm hover:bg-slate-50"
      >
        <span className="min-w-0 truncate font-medium text-slate-800">{nameOf(entry)}</span>
        <span className="shrink-0 text-xs text-slate-500">{meta}</span>
      </Link>
    </li>
  );
}

// One holiday row inside a group: not a link (a location has no profile). Shows the country
// that is out, with the holiday name and "Company Holiday" as its meta.
function HolidayEntryRow({ row }: { row: HolidayRow }) {
  return (
    <li className="-mx-1 flex items-center justify-between gap-3 rounded px-1 py-1 text-sm">
      <span className="min-w-0 truncate font-medium text-slate-800">{row.title}</span>
      <span className="shrink-0 text-xs text-slate-500">{row.meta}</span>
    </li>
  );
}

// A collapsible Who's Out group. Uses a native <details>/<summary> so the summary
// row toggles its list without extra state, reusing SummaryRow for the design-system
// look (mirrors the Onboarding widget's OnboardingGroup).
function WhosOutGroup({
  icon,
  iconClassName,
  title,
  children,
}: {
  icon: ReactNode;
  iconClassName: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <details className="group" open>
      <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden">
        <SummaryRow
          icon={icon}
          iconClassName={iconClassName}
          title={title}
          trailing={<WhosOutDisclosureChevron />}
        />
      </summary>
      <ul className="mt-1 space-y-0.5 pl-2">{children}</ul>
    </details>
  );
}

// Meta for an upcoming-leave row: "Starts <date>" plus the category name when known.
function upcomingMeta(entry: WhosOutEntry): string {
  const starts = `Starts ${formatDayMonthYear(entry.startDate)}`;
  return entry.categoryName ? `${entry.categoryName} · ${starts}` : starts;
}

// Who's Out card (W3). Two collapsible lists, matching the Onboarding widget pattern:
// "Out today" (people on approved time off today, plus one row per country observing a
// public holiday today - the location is what is out, whatever the viewer's own location)
// and "Upcoming" (people whose approved leave starts in the next 7 days). A leave that
// starts today counts as Out today only. Derived client-side from /whos-out (whose
// visibility semantics are unchanged).
export function WhosOutCard() {
  const q = useQuery({
    queryKey: ['home', 'whos-out'],
    queryFn: () => api.get<WhosOutEntry[]>('/v1/home/whos-out'),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const todayIso = new Date().toLocaleDateString('en-CA');
  const entries = Array.isArray(q.data) ? q.data : [];
  const people = peopleOutToday(entries, todayIso);
  const holidays = holidayRowsToday(entries, todayIso);
  const upcoming = upcomingLeaves(entries, todayIso);
  const outTodayCount = people.length + holidays.length;
  const isEmpty = outTodayCount === 0 && upcoming.length === 0;

  return (
    <WidgetCard icon={<WhosOutIcon />} title="Who's Out">
      <CardQueryState
        query={q}
        label="Who's Out"
        skeleton={
          <div className="flex flex-col gap-4">
            <div className="h-[72px] animate-pulse rounded-xl bg-slate-100" />
            <div className="h-[72px] animate-pulse rounded-xl bg-slate-100" />
          </div>
        }
        empty={{
          when: isEmpty,
          node: (
            <div className="flex flex-1 flex-col items-center justify-center py-6 text-center">
              <p className="text-base font-semibold text-slate-900">Nobody is out today. ☀️</p>
            </div>
          ),
        }}
      >
        {/* Height-capped like the Onboarding/Requests lists; extra rows scroll the card. */}
        <ul className="flex max-h-[22rem] flex-col gap-3 overflow-y-auto pr-1">
          {outTodayCount > 0 && (
            <li>
              <WhosOutGroup
                icon={<WhosOutIcon />}
                iconClassName="bg-brand-50 text-brand-hover"
                title={`Out today (${outTodayCount})`}
              >
                {people.map((e) => (
                  <PersonEntryRow
                    key={`p-${e.employeeId}`}
                    entry={e}
                    meta={`Out until ${formatOooUntil(e.endDate)}`}
                  />
                ))}
                {holidays.map((row) => (
                  <HolidayEntryRow key={row.key} row={row} />
                ))}
              </WhosOutGroup>
            </li>
          )}

          {upcoming.length > 0 && (
            <li>
              <WhosOutGroup
                icon={<UpcomingLeaveIcon />}
                iconClassName="bg-amber-50 text-amber-600"
                title={`Upcoming (${upcoming.length})`}
              >
                {upcoming.map((e) => (
                  <PersonEntryRow key={`u-${e.id}`} entry={e} meta={upcomingMeta(e)} />
                ))}
              </WhosOutGroup>
            </li>
          )}
        </ul>
      </CardQueryState>
    </WidgetCard>
  );
}

// Clock icon for the Upcoming group, mirroring the Onboarding widget's unfinished glyph.
function UpcomingLeaveIcon({ className = 'h-[18px] w-[18px]' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden className={className}>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
      <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function WhosOutIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="9" cy="7" r="3" stroke="currentColor" strokeWidth="1.5" />
      <path d="M2 20c0-3.314 3.134-6 7-6s7 2.686 7 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M16 3.13a4 4 0 010 7.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M22 20c0-3.314-1.79-6-4-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

// Party-popper icon for the Company Holidays widget (replaces the 🎉 emoji so
// the card matches the app's inline-SVG icon system). Cone at bottom-left with
// confetti streamers and dots flying out the top-right.
function CompanyHolidaysIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden className={className}>
      <path d="M3 21l5-11.5 6.5 6.5L3 21z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M14 9c0-1.7 1.3-3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M15.5 14c1.7 0 3-1.3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="19.5" cy="5.5" r="1" fill="currentColor" />
      <circle cx="21" cy="10" r="1" fill="currentColor" />
      <circle cx="15" cy="3.5" r="1" fill="currentColor" />
    </svg>
  );
}

// Company Holidays card (W7). Lists every holiday from today through the end of the
// current calendar year as summary rows (the list scrolls when it overflows).
export function CompanyHolidaysCard() {
  const q = useQuery({
    queryKey: ['home', 'company-holidays'],
    queryFn: () => api.get<HolidayDto[]>('/v1/home/company-holidays'),
  });

  const holidays = q.data ?? [];

  return (
    <WidgetCard icon={<CompanyHolidaysIcon className="h-5 w-5" />} title="Company Holidays">
      <CardQueryState
        query={q}
        label="Company Holidays"
        skeleton={
          <div className="flex flex-col gap-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-[64px] animate-pulse rounded-xl bg-slate-100" />
            ))}
          </div>
        }
        empty={{
          when: holidays.length === 0,
          node: (
            <WidgetEmptyState
              image={companyHolidaysEmptyArt}
              title={W7_EMPTY_NO_HOLIDAYS}
            />
          ),
        }}
      >
        {/* Same height cap as the My Direct Reports widget so the two stay in sync;
            tightened ~20% below the other widgets, overflow scrolls the card. */}
        <div className="flex max-h-[17.6rem] flex-col gap-3 overflow-y-auto pr-1">
          {holidays.map((h) => {
            const cleanCodes = presentableCountryCodes(h.countryCodes);
            const codes = cleanCodes.length ? `(${cleanCodes.join(', ')})` : null;
            const holidayDate = parseIsoDate(h.date);
            const dateLabel = holidayDate.toLocaleDateString(undefined, {
              weekday: 'long',
              month: 'short',
              day: 'numeric',
            });
            return (
              <SummaryRow
                key={h.id}
                iconClassName="bg-brand-50"
                icon={<CompanyHolidaysIcon className="h-5 w-5 text-brand-hover" />}
                title={
                  <>
                    {h.name}
                    {codes ? (
                      <span className="ml-1.5 text-xs font-normal text-slate-500">{codes}</span>
                    ) : null}
                  </>
                }
                subtitle={dateLabel}
              />
            );
          })}
        </div>
      </CardQueryState>
    </WidgetCard>
  );
}

function shortDate(iso: string): string {
  return new Date(iso + (iso.includes('T') ? '' : 'T00:00:00')).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

// A span in the card's date style: one date for a single day.
function shortRange(start: string, end: string): string {
  return start === end ? shortDate(start) : `${shortDate(start)} – ${shortDate(end)}`;
}

// Subtitle for a pending-approval row. A PENDING_CHANGE row describes the change the approver is
// deciding on (the proposed dates/days beside the leave they replace, or a cancellation, named from its
// first refunded day when it cuts the leave short).
function pendingApprovalSubtitle(
  r: PendingApprovalEntry['request'],
  catName: string,
  approverName: string | null,
): string {
  const who = `${approverName ?? '—'} · ${catName}`;
  if (r.status === 'PENDING_CHANGE') {
    if (r.proposedCancellation) {
      const range = shortRange(r.startDate, r.endDate);
      const cancelledFrom = partialCancellationFrom(r);
      const cancel = cancelledFrom
        ? `Cancel from ${shortDate(cancelledFrom)} (${range}, ${formatDays(r.days)}d)`
        : `Cancel ${range} (${formatDays(r.days)}d)`;
      return `${who} · ${cancel}${r.proposedCancelReason ? ` · ${r.proposedCancelReason}` : ''}`;
    }
    const proposedRange = shortRange(r.proposedStartDate ?? r.startDate, r.proposedEndDate ?? r.endDate);
    const oldRange = shortRange(r.startDate, r.endDate);
    return `${who} · New: ${proposedRange} · ${formatDays(r.proposedDays ?? r.days)}d (was ${oldRange} · ${formatDays(r.days)}d)`;
  }
  const dateRange = shortRange(r.startDate, r.endDate);
  return `${who} · ${dateRange} · ${formatDays(r.days)}d${r.note ? ` · ${r.note}` : ''} · Submitted ${shortDate(r.createdAt)}`;
}


// Compact Approve/Deny icon button for a pending request row.
function DecisionButton({
  decision,
  employeeName,
  onClick,
}: {
  decision: 'APPROVE' | 'DENY';
  employeeName: string;
  onClick: () => void;
}) {
  const isApprove = decision === 'APPROVE';
  return (
    <Button
      variant={isApprove ? 'brandSoft' : 'dangerSoft'}
      aria-label={`${isApprove ? 'Approve' : 'Deny'} ${employeeName}'s request`}
      title={isApprove ? 'Approve' : 'Deny'}
      onClick={onClick}
      icon={
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
          {isApprove ? (
            <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          ) : (
            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          )}
        </svg>
      }
    />
  );
}

// Time Off Requests card (W8). Reads /home/time-off-requests.
// Admin sees all company-wide pending; manager/employee sees their approver queue.
export function PendingApprovalsCard() {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['home', 'time-off-requests'],
    queryFn: () => api.get<PendingApprovalEntry[]>('/v1/home/time-off-requests'),
  });
  const categories = useTimeOffCategories();

  const entries = q.data ?? [];
  const cats = categories.data ?? [];

  const queryClient = useQueryClient();
  const [decisionFor, setDecisionFor] = useState<{
    request: PendingApprovalEntry['request'];
    decision: 'APPROVE' | 'DENY';
  } | null>(null);

  return (
    <WidgetCard
      icon={
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path d="M9 11l3 3L22 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      }
      title="Time Off Requests"
      rootProps={{ 'data-testid': 'pending-approvals-card' }}
    >
      <CardQueryState
        query={q}
        label="Time Off Requests"
        skeleton={
          <div className="flex flex-col gap-3">
            <div className="h-[68px] animate-pulse rounded-xl bg-slate-100" />
            <div className="h-[68px] animate-pulse rounded-xl bg-slate-100" />
          </div>
        }
        empty={{
          when: entries.length === 0,
          node: (
            <WidgetEmptyState
              image={timeOffRequestsEmptyArt}
              title="All caught up! No pending approvals. ✅"
            />
          ),
        }}
      >
        {/* Capped height: extra requests scroll instead of growing the widget. */}
        <ul
          className="flex max-h-[22rem] flex-col gap-3 overflow-y-auto pr-1"
          data-testid="pending-approvals-cards"
        >
          {entries.map(({ request: r, employeeName, approverName, skippedDates }) => {
            const cat = cats.find((c) => c.id === r.categoryId);
            const catName = cat?.name ?? `#${r.categoryId ?? '?'}`;
            const subtitle = pendingApprovalSubtitle(r, catName, approverName);
            const skips = skipsText(skippedDates, shortDate);
            const isChange = r.status === 'PENDING_CHANGE';
            return (
              <li key={r.id}>
                <SummaryRow
                  icon={<TimeOffIconSvg icon={cat?.icon ?? 'calendar'} className="h-4 w-4" />}
                  iconClassName="bg-brand-50 text-brand-hover"
                  title={
                    <span className="flex items-center gap-2">
                      <Link to={`/people/${r.employeeId}?tab=time-off`} className="hover:underline">
                        {employeeName}
                      </Link>
                      {isChange ? (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
                          {r.proposedCancellation ? 'Cancellation' : 'Change'}
                        </span>
                      ) : null}
                    </span>
                  }
                  subtitle={
                    skips ? (
                      <>
                        {subtitle}
                        <span className="block" data-testid={`skips-${r.id}`}>
                          {skips}
                        </span>
                      </>
                    ) : (
                      subtitle
                    )
                  }
                  subtitleWrap
                  trailing={
                    <div className="flex items-center gap-1.5">
                      <DecisionButton
                        decision="APPROVE"
                        employeeName={employeeName}
                        onClick={() => setDecisionFor({ request: r, decision: 'APPROVE' })}
                      />
                      <DecisionButton
                        decision="DENY"
                        employeeName={employeeName}
                        onClick={() => setDecisionFor({ request: r, decision: 'DENY' })}
                      />
                    </div>
                  }
                />
              </li>
            );
          })}
        </ul>
      </CardQueryState>

      <WidgetCtaButton
        icon={
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M9 11l3 3L22 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        }
        label="Review requests"
        onClick={() => navigate('/approvals')}
      />

      {decisionFor ? (
        <TimeOffDecisionDialog
          request={decisionFor.request}
          decision={decisionFor.decision}
          onClose={() => setDecisionFor(null)}
          onDecided={() => {
            setDecisionFor(null);
            void queryClient.invalidateQueries({ queryKey: ['home'] });
            void queryClient.invalidateQueries({ queryKey: ['time-off'] });
            void queryClient.invalidateQueries({ queryKey: ['calendar'] });
            invalidateLeaveDocuments(queryClient);
          }}
        />
      ) : null}
    </WidgetCard>
  );
}

